import React, { useEffect, useState } from 'react';
import { useConfiguration } from 'src/hooks/Configuration';
import { useToast } from 'src/hooks/Toast';
import { useFinanceCheckFaturas, InvoiceReport, InvoiceChargebackMatch, InvoiceNotConciliatedSale, InvoiceFinanceItem, InvoiceSaleItem } from './useFinanceCheckFaturas';
import handleFinanceReportPDF, { handleFinanceReportPDFWithSaque } from '../../handleFinanceReportPDF';
import downloadit from 'src/utils/download';
import api from 'src/services/api';
import { FaFilePdf, FaFileExcel, FaWallet } from 'react-icons/fa';
import { useTransferPopup } from './popupTransfers';

// ─────────────────────────────────────────────────────────────────────────────
//  Formatação
// ─────────────────────────────────────────────────────────────────────────────

const fmt = (cents: number) =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('pt-BR') : '—';

const fmtDateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

// ─────────────────────────────────────────────────────────────────────────────
//  Design tokens
// ─────────────────────────────────────────────────────────────────────────────

const C = {
  bg:        '#F4F6FA',
  surface:   '#FFFFFF',
  border:    '#E3E8F0',
  text:      '#1A2236',
  muted:     '#8693A8',
  blue:      '#1E3A5F',
  blueLight: '#EBF1FA',
  green:     '#0A7A55',
  greenBg:   '#E6F7F2',
  red:       '#C0392B',
  redBg:     '#FDECEA',
  amber:     '#92400E',
  amberBg:   '#FEF3C7',
  purple:    '#5B21B6',
  purpleBg:  '#EDE9FE',
};

// ─────────────────────────────────────────────────────────────────────────────
//  Primitivos
// ─────────────────────────────────────────────────────────────────────────────

const Card = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', ...style }}>
    {children}
  </div>
);

const CardHeader = ({ label, accent = C.blue, right }: { label: string; accent?: string; right?: React.ReactNode }) => (
  <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
    <div style={{ width: 4, height: 18, borderRadius: 2, background: accent, flexShrink: 0 }} />
    <span style={{ fontWeight: 700, fontSize: 13, color: C.text, letterSpacing: '-0.01em', flex: 1 }}>{label}</span>
    {right}
  </div>
);

const Stat = ({
  label, value, sub, accent = C.blue, size = 'md',
}: {
  label: string; value: string; sub?: string; accent?: string; size?: 'sm' | 'md' | 'lg';
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</div>
    <div style={{ fontSize: size === 'lg' ? 22 : size === 'md' ? 17 : 14, fontWeight: 800, color: accent, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>
      {value}
    </div>
    {sub && <div style={{ fontSize: 11, color: C.muted }}>{sub}</div>}
  </div>
);

const DreRow = ({
  label, value, sign, partial, color, indent, subtotal, highlight,
}: {
  label: string; value: number; sign?: '+' | '−' | '+/−'; partial?: number;
  color?: string; indent?: boolean; subtotal?: boolean; highlight?: boolean;
}) => {
  if (subtotal || highlight) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '11px 20px',
        background: highlight ? C.blue : '#EEF2F8',
        borderTop: `2px solid ${highlight ? C.blue : '#C8D3E8'}`,
        borderBottom: highlight ? undefined : `1px solid ${C.border}`,
      }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: highlight ? '#fff' : C.text }}>{label}</span>
        <span style={{ fontWeight: 800, fontSize: 15, fontVariantNumeric: 'tabular-nums', color: highlight ? (value >= 0 ? '#6EE7B7' : '#FCA5A5') : (value >= 0 ? C.green : C.red) }}>
          {value < 0 ? `−${fmt(Math.abs(value))}` : fmt(value)}
        </span>
      </div>
    );
  }

  const displayValue = value === 0 ? '—'
    : sign === '−' ? `−${fmt(value)}`
    : sign === '+/−' ? (value > 0 ? `+${fmt(value)}` : value < 0 ? `−${fmt(Math.abs(value))}` : '—')
    : fmt(value);

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr auto auto', alignItems: 'center',
      gap: '0 16px', padding: '9px 20px', paddingLeft: indent ? 36 : 20,
      borderBottom: `1px solid ${C.border}`,
    }}>
      <span style={{ fontSize: 12.5, color: value === 0 ? C.muted : C.text }}>
        {sign && <span style={{ color: C.muted, marginRight: 6, fontWeight: 700, fontSize: 11 }}>({sign})</span>}
        {label}
      </span>
      <span style={{ fontWeight: 600, fontSize: 13, textAlign: 'right', minWidth: 110, fontVariantNumeric: 'tabular-nums', color: color ?? (value === 0 ? C.muted : C.text) }}>
        {displayValue}
      </span>
      {partial !== undefined && (
        <span style={{ fontSize: 11, color: C.muted, textAlign: 'right', minWidth: 110, fontVariantNumeric: 'tabular-nums' }}>
          {partial < 0 ? `−${fmt(Math.abs(partial))}` : fmt(partial)}
        </span>
      )}
    </div>
  );
};

const Pill = ({ label, color, bg }: { label: string; color: string; bg: string }) => (
  <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color, background: bg }}>
    {label}
  </span>
);

const Th = ({ children, align }: { children: React.ReactNode; align?: 'right' | 'left' }) => (
  <th style={{ padding: '9px 14px', background: '#F1F5FB', color: C.muted, fontWeight: 700, fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: align ?? 'left', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>
    {children}
  </th>
);

const Td = ({
  children, align, color, bold, muted, nowrap, noWrap, style,
}: {
  children?: React.ReactNode; align?: 'right' | 'left'; color?: string;
  bold?: boolean; muted?: boolean; nowrap?: boolean; noWrap?: boolean; style?: React.CSSProperties;
}) => (
  <td style={{
    padding: '8px 14px', fontSize: 12.5,
    textAlign: align ?? 'left',
    fontVariantNumeric: align === 'right' ? 'tabular-nums' : undefined,
    color: color ?? (muted ? C.muted : C.text),
    fontWeight: bold ? 700 : undefined,
    whiteSpace: (nowrap || noWrap) ? 'nowrap' : undefined,
    borderBottom: `1px solid #F0F2F7`,
    ...style,
  }}>
    {children}
  </td>
);

const Table = ({ headers, children }: { headers: string[]; children: React.ReactNode }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
      <thead>
        <tr>
          {headers.map(h => <Th key={h} align={['Bruto', 'Líquido', 'Net Total', 'Valor Parcela', 'Saldo Após', 'Saldo', 'Saldo Anterior', 'Saldo Esperado', 'Saldo Real', 'Diferença', 'Taxa TED', 'Líquido Sacado'].includes(h) ? 'right' : 'left'}>{h}</Th>)}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const TabBtn = ({ label, active, badge, onClick }: { label: string; active: boolean; badge?: number; onClick: () => void }) => (
  <button
    onClick={onClick}
    style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: active ? C.blue : 'transparent', color: active ? '#fff' : C.muted, fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
  >
    {label}
    {badge !== undefined && badge > 0 && (
      <span style={{ background: active ? 'rgba(255,255,255,0.25)' : C.red, color: '#fff', borderRadius: 999, fontSize: 10, fontWeight: 800, padding: '1px 6px' }}>
        {badge}
      </span>
    )}
  </button>
);

const LegendaDot = ({ color, label }: { color: string; label: string }) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#000' }}>
    <span style={{ width: 10, height: 10, borderRadius: 2, background: color, border: `1.5px solid ${color}`, display: 'inline-block', opacity: 0.7 }} />
    {label}
  </span>
);

// ─────────────────────────────────────────────────────────────────────────────
//  BrutoTable — composição do bruto em faturas: parcelas + juros/multa
// ─────────────────────────────────────────────────────────────────────────────

const BrutoTable = ({ r }: { r: InvoiceReport }) => (
  <Card>
    <CardHeader label="Composição do Bruto Conciliado" />
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ background: '#f8fafc' }}>
          {['Origem', 'Valor', 'Obs.'].map(h => (
            <th key={h} style={{ padding: '9px 16px', textAlign: h === 'Valor' ? 'right' : 'left', fontWeight: 700, fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${C.border}` }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr style={{ borderBottom: `1px solid ${C.border}` }}>
          <td style={{ padding: '10px 16px', fontWeight: 600, color: C.text }}>Parcelas (installment_value)</td>
          <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 700, color: C.blue, fontVariantNumeric: 'tabular-nums' }}>{fmt(r.totalInstallmentValue)}</td>
          <td style={{ padding: '10px 16px', color: C.muted, fontSize: 12 }}>Σ installment_value dos créditos</td>
        </tr>
        {r.totalJurosMulta > 0 && (
          <tr style={{ borderBottom: `1px solid ${C.border}` }}>
            <td style={{ padding: '10px 16px', fontWeight: 600, color: C.text }}>Juros e multa de atraso</td>
            <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 700, color: C.green, fontVariantNumeric: 'tabular-nums' }}>+{fmt(r.totalJurosMulta)}</td>
            <td style={{ padding: '10px 16px', color: C.muted, fontSize: 12 }}>Embutidos em item.total pelo backend</td>
          </tr>
        )}
        <tr style={{ background: C.blueLight }}>
          <td style={{ padding: '11px 16px', fontWeight: 800, color: C.blue }}>Bruto Total Conciliado</td>
          <td style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 800, color: C.blue, fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>{fmt(r.brutoConciliated)}</td>
          <td style={{ padding: '11px 16px', color: C.muted, fontSize: 12 }}>Σ operation_type_raw = credit</td>
        </tr>
      </tbody>
    </table>
  </Card>
);

// ─────────────────────────────────────────────────────────────────────────────
//  ReconciliationBanner — parcelas pagas vs extrato
// ─────────────────────────────────────────────────────────────────────────────

const ReconciliationBanner = ({ r }: { r: InvoiceReport }) => {
  const diff = r.totalSalesInstallmentValue - r.totalInstallmentValue;
  const ok   = diff === 0;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 20px', background: ok ? C.greenBg : C.amberBg, border: `1px solid ${ok ? '#6EE7B7' : '#FCD34D'}`, borderRadius: 10 }}>
      <span style={{ fontSize: 22, lineHeight: 1 }}>{ok ? '✅' : '⚠️'}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: ok ? C.green : C.amber, marginBottom: 8 }}>
          {ok ? 'Parcelas pagas batem com o extrato' : 'Divergência entre faturas pagas e extrato'}
        </div>
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          {[
            { label: 'Parcelas pagas (plataforma)', value: fmt(r.totalSalesInstallmentValue), sub: 'Σ installment_value, payment_status=paid' },
            { label: 'Parcelas conciliadas (extrato)', value: fmt(r.totalInstallmentValue), sub: 'Σ installment_value dos créditos' },
            {
              label: 'Diferença',
              value: diff === 0 ? 'R$ 0,00' : (diff > 0 ? `+${fmt(diff)}` : `−${fmt(Math.abs(diff))}`),
              sub: diff > 0 ? 'Faturas pagas sem crédito no extrato' : diff < 0 ? 'Créditos sem fatura correspondente' : 'Valores idênticos',
              accent: ok ? C.green : diff > 0 ? C.red : C.blue,
            },
          ].map(s => (
            <div key={s.label} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{s.label}</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: (s as any).accent ?? C.text, fontVariantNumeric: 'tabular-nums' }}>{s.value}</div>
              <div style={{ fontSize: 11, color: C.muted }}>{s.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  DRE — Faturas (sem antecipation_gap, taxaTotal como âncora)
// ─────────────────────────────────────────────────────────────────────────────

const DreSection = ({ r }: { r: InvoiceReport }) => {
  const d = r.deducoes;

  // Em faturas: taxaTotal = Σ(total − net_total) — dedução total do gateway.
  // Por construção: liquidoCreditEntries = brutoConciliated − taxaTotal sempre.
  const calculado      = r.brutoConciliated - d.taxaTotal;
  const discrepancia   = calculado - r.liquidoCreditEntries;
  const temDiscrepancia = Math.abs(discrepancia) > 1;

  // Partials acumulados — coluna direita: saldo corrente após cada dedução
  let p = r.brutoConciliated;
  const p2 = (p -= d.jurosParcelamento);
  const p3 = (p -= d.taxaCartao);
  const p4 = (p -= d.taxaAdministrativa);
  const p5 = (p -= d.outrasDeducoes);   // = liquidoCreditEntries

  // Continua abaixo do Líquido
  const liq = r.liquidoCreditEntries;
  const p6  = liq - r.chargebackDebitTotal;
  const p7  = p6  + r.chargebackRefundTotal;
  const p8  = p7  - r.totalTaxasTED;
  const p9  = p8  - (r.totalSacado - r.totalTaxasTED);

  return (
    <Card>
      <CardHeader label="Demonstrativo de Resultado" />

      {temDiscrepancia && (
        <div style={{ margin: '12px 16px', padding: '10px 14px', background: C.amberBg, border: `1px solid #FCD34D`, borderRadius: 8, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 18, lineHeight: 1 }}>⚠️</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: C.amber }}>Diferença não explicada nas deduções</div>
            <div style={{ fontSize: 12, color: C.amber, marginTop: 3, lineHeight: 1.6 }}>
              A soma das deduções resulta em <strong>{fmt(calculado)}</strong>, mas o líquido real é <strong>{fmt(r.liquidoCreditEntries)}</strong>.{' '}
              Diferença: <strong>{discrepancia > 0 ? '+' : '−'}{fmt(Math.abs(discrepancia))}</strong>.
            </div>
          </div>
        </div>
      )}

      <DreRow label="Bruto Total Conciliado"   value={r.brutoConciliated}   sign="+" />
      <DreRow label="Juros de Parcelamento"     value={d.jurosParcelamento}  sign="−" indent color={C.red} partial={p2} />
      <DreRow label="Taxa de Cartão de Crédito" value={d.taxaCartao}         sign="−" indent color={C.red} partial={p3} />
      <DreRow label="Taxa Administrativa"       value={d.taxaAdministrativa} sign="−" indent color={C.red} partial={p4} />
      <DreRow label="Taxa de Venda e Outros"    value={d.outrasDeducoes}     sign="−" indent color={C.red} partial={p5} />

      {temDiscrepancia ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 20px', background: '#EEF2F8', borderTop: '2px solid #C8D3E8', borderBottom: `1px solid ${C.border}` }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: C.text, display: 'flex', alignItems: 'center', gap: 8 }}>
            Líquido Final
            <span style={{ fontSize: 10, background: C.amberBg, color: C.amber, borderRadius: 999, padding: '2px 8px', fontWeight: 700, border: `1px solid #FCD34D` }}>✗ Diverge</span>
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
            <span style={{ fontWeight: 800, fontSize: 15, fontVariantNumeric: 'tabular-nums', color: C.green }}>{fmt(r.liquidoCreditEntries)}</span>
            <span style={{ fontSize: 11, color: C.amber, fontVariantNumeric: 'tabular-nums' }}>
              calculado: {fmt(calculado)}{' '}
              <span style={{ fontWeight: 700 }}>({discrepancia > 0 ? '+' : '−'}{fmt(Math.abs(discrepancia))})</span>
            </span>
          </div>
        </div>
      ) : (
        <DreRow label="Líquido Final" value={r.liquidoCreditEntries} subtotal />
      )}

      <div style={{ height: 1, background: C.border }} />

      {/* Chargebacks — abaixo do Líquido, na seção de fluxo de caixa */}
      <DreRow label="Chargebacks / Estornos (débito)"  value={r.chargebackDebitTotal}  sign="−" indent color={C.red}   partial={p6} />
      <DreRow label="Devolução de Taxas (marketplace)" value={r.chargebackRefundTotal} sign="+" indent color={C.green} partial={p7} />

      {/* Saques */}
      <DreRow label="Taxas de Saque (TED)"                          value={r.totalTaxasTED}                sign="−" indent color={C.red}    partial={p8} />
      <DreRow label={`Já Sacado (${r.saqueEntries.length} TEDs)`}   value={r.totalSacado - r.totalTaxasTED} sign="−" color={C.purple}        partial={p9} />

      <DreRow label="Saldo Atual" value={r.saldoAtual} highlight />
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Payload para análise por IA
// ─────────────────────────────────────────────────────────────────────────────

const buildAIPayload = (report: InvoiceReport, financeData: InvoiceFinanceItem[]): string => {
  return JSON.stringify({
    resumo: {
      totalParcelas:            report.totalSalesInstallmentValue,
      totalConciliado:          report.totalConciliated,
      totalNaoConciliado:       report.totalNotConciliated,
      brutoConciliated:         report.brutoConciliated,
      totalInstallmentValue:    report.totalInstallmentValue,
      totalJurosMulta:          report.totalJurosMulta,
      liquidoTotal:             report.liquidoTotal,
      totalSacado:              report.totalSacado,
      totalTaxasTED:            report.totalTaxasTED,
      saldoAtual:               report.saldoAtual,
      qtdChargebacks:           report.chargebacks.length,
      chargebackDebitTotal:     report.chargebackDebitTotal,
      chargebackRefundTotal:    report.chargebackRefundTotal,
      netChargebackImpact:      report.netChargebackImpact,
      custoOperacaoChargebacks: report.totalCustoOperacaoChargebacks,
      deducoes:                 report.deducoes,
    },
    chargebacks: report.chargebacks.map(cb => ({
      hash:               cb.chargebackEntry.hash,
      data:               cb.chargebackEntry.date,
      cliente:            cb.sale?.payer_id_response?.name ?? cb.sale?.client_id_response?.name ?? null,
      metodo:             cb.chargebackEntry.payment_method_id,
      saleBruto:          cb.saleBruto,
      creditoLiquido:     cb.creditoLiquido,
      debitadoChargeback: cb.debitadoChargeback,
      taxasDevolvidas:    cb.taxasDevolvidas,
      custoOperacao:      cb.custoOperacao,
      prejuizoTotal:      cb.prejuizoTotal,
    })),
    naoConciliadas: report.salesNotConciliated.map(s => ({
      hash:         s.sale.invoice_hash ?? s.sale.sale_hash,
      cliente:      s.sale.payer_id_response?.name ?? s.sale.client_id_response?.name ?? null,
      metodo:       s.sale.payment_method_id,
      parcela:      s.sale.installment_value,
      aprovadoEm:   s.sale.approved_at,
      disponivelEm: s.estimatedAvailableAt?.toISOString() ?? null,
      atrasada:     s.isPastDeadline,
    })),
    extrato: financeData.map(f => ({
      id:              f.item_id,
      hash:            f.hash,
      data:            f.date,
      tipo:            f.operation_type_raw,
      descricao:       f.operation_type,
      cliente:         f.client,
      bruto:           f.total,
      installment:     f.installment_value,
      liquido:         f.net_total,
      saldo:           f.saldo,
      taxaTotal:       f.tax,
      taxaAdmin:       f.administration_tax,
      taxaCartao:      f.transaction_tax,
      jurosParc:       f.fees,
      taxaTED:         f.operation_fee,
      metodo:          f.payment_method_id,
    })),
  }, null, 2);
};

// ─────────────────────────────────────────────────────────────────────────────
//  Painel de Análise por IA
// ─────────────────────────────────────────────────────────────────────────────

const AIAnalysisPanel = ({
  report,
  financeData,
  onClose,
}: {
  report: InvoiceReport;
  financeData: InvoiceFinanceItem[];
  onClose: () => void;
}) => {
  const downloadJSON = () => {
    const payload = buildAIPayload(report, financeData);
    const blob    = new Blob([payload], { type: 'application/json' });
    const url     = URL.createObjectURL(blob);
    const a       = document.createElement('a');
    a.href        = url;
    a.download    = `auditoria-faturas-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 999 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 420, maxWidth: '95vw', background: C.surface, zIndex: 1000, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 32px rgba(30,58,95,0.15)', overflowY: 'auto' }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 22 }}>📦</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: C.text }}>Arquivo de auditoria</div>
            <div style={{ fontSize: 12, color: C.muted }}>Exportar JSON completo das faturas</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 22, cursor: 'pointer', color: C.muted }}>×</button>
        </div>
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p style={{ color: C.muted, fontSize: 13, margin: 0, lineHeight: 1.6 }}>
            O arquivo contém resumo, deduções, chargebacks, faturas não conciliadas e o extrato completo linha a linha — pronto para auditoria externa ou análise por IA.
          </p>
          <button
            onClick={downloadJSON}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 20px', borderRadius: 8, border: 'none', background: C.purple, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
          >
            ⬇ Baixar JSON de auditoria
          </button>
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Drawer: histórico de compras do cliente (faturas)
// ─────────────────────────────────────────────────────────────────────────────

const UserSalesDrawer = ({
  clientId, clientName, currentProjectUrl, onClose,
}: {
  clientId: string | number; clientName: string; currentProjectUrl: string; onClose: () => void;
}) => {
  const [sales, setSales]           = useState<any[]>([]);
  const [projectMap, setProjectMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [salesRes, projectsRes] = await Promise.all([
          api.get('/sales-invoice', { params: { where: { '$or': [{ payer_id: clientId }, { client_id: clientId }] } } }),
          api.get('/products', { params: { limitItems: 1000 } }),
        ]);
        const map = new Map<string, string>();
        for (const p of (projectsRes?.data?.rows ?? [])) if (p.url) map.set(p.url, p.title ?? p.url);
        setSales(salesRes?.data?.rows ?? []);
        setProjectMap(map);
      } catch { setError(true); }
      finally { setLoading(false); }
    })();
  }, [clientId]);

  const thisProject  = sales.filter((s: any) => s.project_id === currentProjectUrl);
  const otherProject = sales.filter((s: any) => s.project_id !== currentProjectUrl);

  const SalesTable = ({ rows, showProject }: { rows: any[]; showProject: boolean }) => {
    const headers = ['Data', 'Invoice Hash', ...(showProject ? ['Projeto'] : []), 'Método', 'Parcela', 'Status'];
    return (
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
        <thead>
          <tr>{headers.map(h => <th key={h} style={{ padding: '8px 14px', background: '#F1F5FB', color: C.muted, fontWeight: 700, fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: h === 'Parcela' ? 'right' : 'left', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1 }}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((s: any, i: number) => {
            const statusColor: Record<string, string> = { paid: C.green, cancelled: C.red, waiting: C.amber };
            const statusLabel: Record<string, string> = { paid: 'Pago', cancelled: 'Cancelado', waiting: 'Aguardando' };
            return (
              <tr key={i} style={{ borderBottom: `1px solid #F0F2F7` }}>
                <td style={{ padding: '8px 14px', color: C.muted, whiteSpace: 'nowrap' }}>{fmtDate(s.approved_at ?? s.createdAt)}</td>
                <td style={{ padding: '8px 14px', color: C.muted }}><code style={{ fontSize: 10 }}>{(s.invoice_hash ?? '—').slice(0, 14)}…</code></td>
                {showProject && <td style={{ padding: '8px 14px', color: C.muted, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{projectMap.get(s.project_id) ?? s.project_id ?? '—'}</td>}
                <td style={{ padding: '8px 14px', color: C.muted }}>{s.payment_method_id ?? '—'}</td>
                <td style={{ padding: '8px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'rgb(26, 34, 54)' }}>{fmt(s.installment_value ?? s.total ?? 0)}</td>
                <td style={{ padding: '8px 14px' }}>
                  <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: statusColor[s.payment_status_id] ?? C.muted, background: s.payment_status_id === 'paid' ? C.greenBg : s.payment_status_id === 'cancelled' ? C.redBg : C.amberBg }}>
                    {statusLabel[s.payment_status_id] ?? s.payment_status_id ?? '—'}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 999 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 580, maxWidth: '96vw', background: '#fff', zIndex: 1000, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 32px rgba(30,58,95,0.15)' }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <span style={{ fontSize: 22 }}>👤</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: C.text }}>{clientName}</div>
            <div style={{ fontSize: 12, color: C.muted }}>Histórico de faturas do cliente</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 22, cursor: 'pointer', color: C.muted }}>×</button>
        </div>

        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 12, color: C.muted }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', border: `2px solid ${C.border}`, borderTopColor: C.blue, animation: 'spin 0.8s linear infinite' }} />
            Carregando faturas…
          </div>
        ) : error ? (
          <div style={{ padding: 24, color: C.red }}>Erro ao carregar faturas do cliente.</div>
        ) : sales.length === 0 ? (
          <div style={{ padding: 24, color: C.muted }}>Nenhuma fatura encontrada para este cliente.</div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {thisProject.length > 0 && (
              <div>
                <div style={{ padding: '12px 20px 6px', fontWeight: 700, fontSize: 12, color: C.blue, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Este projeto ({thisProject.length})
                </div>
                <SalesTable rows={thisProject} showProject={false} />
              </div>
            )}
            {otherProject.length > 0 && (
              <div>
                <div style={{ padding: '12px 20px 6px', fontWeight: 700, fontSize: 12, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Outros projetos ({otherProject.length})
                </div>
                <SalesTable rows={otherProject} showProject />
              </div>
            )}
          </div>
        )}

        <div style={{ padding: '12px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 16, fontSize: 12, color: C.muted, flexShrink: 0 }}>
          <span>Total: <strong style={{ color: C.text }}>{sales.length}</strong> faturas</span>
          <span>Pagas: <strong style={{ color: C.green }}>{sales.filter((s: any) => s.payment_status_id === 'paid').length}</strong></span>
          <span>Canceladas: <strong style={{ color: C.red }}>{sales.filter((s: any) => s.payment_status_id === 'cancelled').length}</strong></span>
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Não Conciliadas
// ─────────────────────────────────────────────────────────────────────────────

const NotConciliatedSection = ({ items }: { items: InvoiceNotConciliatedSale[] }) => {
  const atrasadas  = items.filter(i => i.isPastDeadline);
  const aguardando = items.filter(i => !i.isPastDeadline);

  if (items.length === 0) {
    return <p style={{ color: C.green, fontWeight: 600, padding: 4 }}>Todas as faturas pagas estão conciliadas. ✅</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {atrasadas.length > 0 && (
        <Card>
          <CardHeader label={`${atrasadas.length} fatura(s) fora do prazo — verificação necessária`} accent={C.red} />
          <Table headers={['Criação', 'Aprovação', 'Cliente', 'Método', 'Parcela', 'Invoice Hash', 'Prazo Estimado', 'Situação']}>
            {atrasadas.map(({ sale, estimatedAvailableAt }) => (
              <tr key={sale._id}>
                <Td nowrap muted>{fmtDate(sale.createdAt)}</Td>
                <Td nowrap muted>{fmtDateTime(sale.approved_at)}</Td>
                <Td bold>{sale.payer_id_response?.name ?? sale.client_id_response?.name ?? '—'}</Td>
                <Td>{sale.payment_method_id}</Td>
                <Td align="right">{fmt(sale.installment_value ?? 0)}</Td>
                <Td muted><code style={{ fontSize: 11 }}>{sale.invoice_hash ?? sale.sale_hash ?? '—'}</code></Td>
                <Td nowrap>{estimatedAvailableAt ? fmtDate(estimatedAvailableAt.toISOString()) : '—'}</Td>
                <Td><Pill label="Atrasada" color={C.red} bg={C.redBg} /></Td>
              </tr>
            ))}
          </Table>
          <div style={{ padding: '10px 14px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
            <span style={{ fontSize: 12, color: C.muted }}>Total:</span>
            <strong style={{ color: C.red }}>{fmt(atrasadas.reduce((a, i) => a + (i.sale.installment_value ?? 0), 0))}</strong>
          </div>
        </Card>
      )}
      {aguardando.length > 0 && (
        <Card>
          <CardHeader label={`${aguardando.length} fatura(s) dentro do prazo de compensação`} accent={C.amber} />
          <Table headers={['Criação', 'Aprovação', 'Cliente', 'Método', 'Parcela', 'Invoice Hash', 'Disponível em']}>
            {aguardando.map(({ sale, estimatedAvailableAt }) => (
              <tr key={sale._id}>
                <Td nowrap muted>{fmtDate(sale.createdAt)}</Td>
                <Td nowrap muted>{fmtDateTime(sale.approved_at)}</Td>
                <Td bold>{sale.payer_id_response?.name ?? sale.client_id_response?.name ?? '—'}</Td>
                <Td>{sale.payment_method_id}</Td>
                <Td align="right">{fmt(sale.installment_value ?? 0)}</Td>
                <Td muted><code style={{ fontSize: 11 }}>{sale.invoice_hash ?? sale.sale_hash ?? '—'}</code></Td>
                <Td nowrap>{estimatedAvailableAt ? fmtDate(estimatedAvailableAt.toISOString()) : '—'}</Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Divergências (chargebacks + créditos órfãos)
// ─────────────────────────────────────────────────────────────────────────────

const DivergenceSection = ({
  chargebacks, creditosOrfaos, onViewUserSales,
}: {
  chargebacks: InvoiceChargebackMatch[];
  creditosOrfaos: InvoiceFinanceItem[];
  onViewUserSales: (clientId: string | number, clientName: string) => void;
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

    {creditosOrfaos.length > 0 && (
      <Card>
        <CardHeader
          label={`${creditosOrfaos.length} entrada${creditosOrfaos.length !== 1 ? 's' : ''} no extrato sem fatura correspondente`}
          accent={C.purple}
          right={<span style={{ fontSize: 12, color: C.muted }}>Total:&nbsp;<strong style={{ color: C.purple }}>{fmt(creditosOrfaos.reduce((a, f) => a + (f.total ?? 0), 0))}</strong></span>}
        />
        <Table headers={['Data', 'Cliente', 'Hash', 'Método', 'Bruto', 'Líquido', 'Ver compras']}>
          {creditosOrfaos.map((f, i) => (
            <tr key={i} style={{ background: C.purpleBg }}>
              <Td nowrap muted>{fmtDate(f.date)}</Td>
              <Td bold>{f.client || '—'}</Td>
              <Td muted><code style={{ fontSize: 11 }}>{f.hash}</code></Td>
              <Td muted nowrap>{f.payment_method_id || '—'}</Td>
              <Td align="right">{fmt(f.total ?? 0)}</Td>
              <Td align="right" color={C.green}>{fmt(f.net_total)}</Td>
              <Td>
                {f.client_id && (
                  <button onClick={() => onViewUserSales(f.client_id!, f.client || 'Cliente')}
                    style={{ border: 'none', background: C.purpleBg, color: C.purple, cursor: 'pointer', fontWeight: 700, fontSize: 12, borderRadius: 6, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 4 }}>
                    👤 Ver compras
                  </button>
                )}
              </Td>
            </tr>
          ))}
        </Table>
      </Card>
    )}

    <Card>
      <CardHeader
        label={`${chargebacks.length} Chargeback${chargebacks.length !== 1 ? 's' : ''} / Estorno${chargebacks.length !== 1 ? 's' : ''}`}
        accent={C.red}
        right={chargebacks.length > 0 ? <span style={{ fontSize: 12, color: C.muted }}>Custo total:&nbsp;<strong style={{ color: C.red }}>{fmt(chargebacks.reduce((a, c) => a + Math.max(0, c.custoOperacao), 0))}</strong></span> : undefined}
      />
      {chargebacks.length === 0
        ? <p style={{ padding: '16px 20px', color: C.green, fontWeight: 600, margin: 0 }}>Nenhum chargeback ou estorno identificado. ✅</p>
        : (() => {
            const sumBruto    = chargebacks.reduce((a, c) => a + c.saleBruto, 0);
            const sumLiquido  = chargebacks.reduce((a, c) => a + c.creditoLiquido, 0);
            const sumEstorno  = chargebacks.reduce((a, c) => a + c.debitadoChargeback, 0);
            const sumDvsBruto = chargebacks.reduce((a, c) => a + (c.debitadoChargeback - c.saleBruto), 0);
            const sumDvsLiq   = chargebacks.reduce((a, c) => a + (c.debitadoChargeback - c.creditoLiquido), 0);
            const sumCusto    = chargebacks.reduce((a, c) => a + Math.max(0, c.custoOperacao), 0);
            const tfootTd = (v: number, pos?: boolean, neg?: boolean) => {
              const color = pos ? C.red : neg ? C.green : C.text;
              return (
                <td style={{ padding: '9px 14px', fontWeight: 800, fontSize: 12.5, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color, background: '#FEF2F2', borderTop: `2px solid #FECACA`, whiteSpace: 'nowrap' }}>
                  {v === 0 ? '—' : pos ? `+${fmt(Math.abs(v))}` : neg ? `−${fmt(Math.abs(v))}` : fmt(Math.abs(v))}
                </td>
              );
            };
            return (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr>
                      {['Data', 'Cliente', 'Hash', 'Método', 'Bruto', 'Líquido Creditado', 'Estorno', 'Δ vs Bruto', 'Δ vs Líquido', 'Custo Op.', 'Ver compras'].map((h, i) => (
                        <th key={i} style={{ padding: '9px 14px', background: '#F1F5FB', color: C.muted, fontWeight: 700, fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: 'left', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {chargebacks.map((cb, i) => {
                      const cliente  = cb.sale?.payer_id_response?.name ?? cb.sale?.client_id_response?.name ?? '—';
                      const dBruto   = cb.debitadoChargeback - cb.saleBruto;
                      const dLiquido = cb.debitadoChargeback - cb.creditoLiquido;
                      const cid      = (cb.chargebackEntry as any).client_id ?? (cb.sale as any)?.client_id ?? (cb.sale as any)?.payer_id;
                      return (
                        <tr key={i} style={{ background: '#FFF5F5' }}>
                          <Td nowrap muted>{fmtDate(cb.chargebackEntry.date)}</Td>
                          <Td bold>{cliente}</Td>
                          <Td muted><code style={{ fontSize: 11 }}>{cb.chargebackEntry.hash}</code></Td>
                          <Td muted nowrap>{cb.chargebackEntry.payment_method_id || cb.sale?.payment_method_id || '—'}</Td>
                          <Td align="right">{cb.saleBruto > 0 ? fmt(cb.saleBruto) : '—'}</Td>
                          <Td align="right" color={C.green}>{fmt(cb.creditoLiquido)}</Td>
                          <Td align="right" color={C.red}>−{fmt(cb.debitadoChargeback)}</Td>
                          <Td align="right" color={dBruto   > 0 ? C.red : C.green} bold>{dBruto > 0 ? `+${fmt(dBruto)}` : dBruto < 0 ? `−${fmt(Math.abs(dBruto))}` : '—'}</Td>
                          <Td align="right" color={dLiquido > 0 ? C.red : C.green} bold>{dLiquido > 0 ? `+${fmt(dLiquido)}` : dLiquido < 0 ? `−${fmt(Math.abs(dLiquido))}` : '—'}</Td>
                          <Td align="right" color={cb.custoOperacao > 0 ? C.red : C.muted}>{cb.custoOperacao > 0 ? `−${fmt(cb.custoOperacao)}` : '—'}</Td>
                          <Td>
                            {cid && (
                              <button onClick={() => onViewUserSales(cid, cliente)}
                                style={{ border: 'none', background: '#FFF5F5', color: C.red, cursor: 'pointer', fontWeight: 700, fontSize: 12, borderRadius: 6, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 4 }}>
                                👤 Ver compras
                              </button>
                            )}
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={4} style={{ padding: '9px 14px', fontWeight: 800, fontSize: 11, color: C.muted, background: '#FEF2F2', borderTop: `2px solid #FECACA`, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Totais ({chargebacks.length})</td>
                      {tfootTd(sumBruto)}
                      {tfootTd(sumLiquido)}
                      <td style={{ padding: '9px 14px', fontWeight: 800, fontSize: 12.5, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: C.red, background: '#FEF2F2', borderTop: `2px solid #FECACA` }}>−{fmt(sumEstorno)}</td>
                      {tfootTd(sumDvsBruto, sumDvsBruto > 0, sumDvsBruto < 0)}
                      {tfootTd(sumDvsLiq,   sumDvsLiq   > 0, sumDvsLiq   < 0)}
                      <td style={{ padding: '9px 14px', fontWeight: 800, fontSize: 12.5, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: C.red, background: '#FEF2F2', borderTop: `2px solid #FECACA` }}>{sumCusto > 0 ? `−${fmt(sumCusto)}` : '—'}</td>
                      <td style={{ background: '#FEF2F2', borderTop: `2px solid #FECACA` }} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })()
      }
    </Card>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
//  Inconsistências de saldo (fim da capa)
// ─────────────────────────────────────────────────────────────────────────────

const SaldoInconsistenciesSection = ({ items }: { items: InvoiceReport['saldoInconsistencies'] }) => {
  if (items.length === 0) return null;
  return (
    <Card>
      <CardHeader
        label={`${items.length} inconsistência${items.length !== 1 ? 's' : ''} de saldo detectada${items.length !== 1 ? 's' : ''}`}
        accent={C.red}
        right={<span style={{ fontSize: 11, background: C.redBg, color: C.red, borderRadius: 999, padding: '2px 10px', fontWeight: 700 }}>⚠️ Verificação necessária</span>}
      />
      <div style={{ padding: '10px 20px 6px', fontSize: 12, color: C.muted, lineHeight: 1.6, borderBottom: `1px solid ${C.border}` }}>
        Para cada linha, o cálculo esperado é: <strong>saldo anterior + net_total = saldo</strong>. As linhas abaixo não obedecem essa relação (tolerância: 1 centavo).
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr>
              {['Data', 'Operação', 'Hash', 'Net Total', 'Saldo Anterior', 'Saldo Esperado', 'Saldo Real', 'Diferença'].map(h => (
                <th key={h} style={{ padding: '9px 14px', background: '#FEF2F2', color: C.red, fontWeight: 700, fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: ['Data', 'Operação', 'Hash'].includes(h) ? 'left' : 'right', borderBottom: `2px solid #FECACA`, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((inc, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#FFF5F5' : '#fff', borderBottom: `1px solid #FEE2E2` }}>
                <Td nowrap muted>{fmtDateTime(inc.item.dateRef ?? inc.item.date)}</Td>
                <Td>{inc.item.operation_type || inc.item.operation_type_raw || '—'}</Td>
                <Td muted><code style={{ fontSize: 10, background: '#FEE2E2', padding: '1px 5px', borderRadius: 3 }}>{inc.item.hash || '—'}</code></Td>
                <Td align="right" color={inc.item.net_total >= 0 ? C.green : C.red} bold>
                  {inc.item.net_total >= 0 ? '+' : '−'}{fmt(Math.abs(inc.item.net_total))}
                </Td>
                <Td align="right" muted>{fmt(inc.prevSaldo)}</Td>
                <Td align="right" muted>{fmt(inc.expectedSaldo)}</Td>
                <Td align="right" bold>{fmt(inc.item.saldo ?? 0)}</Td>
                <Td align="right" color={inc.diff > 0 ? C.green : C.red} bold>
                  {inc.diff > 0 ? '+' : '−'}{fmt(Math.abs(inc.diff))}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Linha a Linha — accordion por período de saque
// ─────────────────────────────────────────────────────────────────────────────

interface PeriodSummaryFaturas {
  bruto: number;
  sobrantesInicio: number;
  installmentValue: number;
  jurosMulta: number;
  jurosParcelamento: number;
  taxaTotal: number;
  liquido: number;
  taxaSaque: number;
  valorSaque: number;
  saldoFinal: number;
}

const SaquePeriodSummary = ({ s, label }: { s: PeriodSummaryFaturas; label: string }) => {
  const cols: Array<{ label: string; value: number | null; color?: string }> = [
    { label: 'Bruto',               value: s.bruto },
    { label: 'Parcelas',            value: s.installmentValue },
    { label: 'Juros/Multa Atraso',  value: s.jurosMulta > 0 ? s.jurosMulta : 0, color: s.jurosMulta > 0 ? C.green : undefined },
    { label: 'Saldo Anterior',      value: s.sobrantesInicio, color: s.sobrantesInicio !== 0 ? C.blue : undefined },
    { label: 'Juros Parcelamento',  value: s.jurosParcelamento > 0 ? -s.jurosParcelamento : 0, color: C.red },
    { label: 'Taxa Total Gateway',  value: s.taxaTotal > 0 ? -s.taxaTotal : 0, color: C.red },
    { label: 'Líquido',             value: s.liquido, color: C.green },
    { label: 'Taxa de Saque',       value: s.taxaSaque > 0 ? -s.taxaSaque : 0, color: C.red },
    { label: 'Valor do Saque',      value: s.valorSaque > 0 ? -s.valorSaque : null, color: C.purple },
    { label: 'Saldo',               value: s.saldoFinal, color: s.saldoFinal < 0 ? C.red : C.muted },
  ];

  return (
    <div style={{ padding: '12px 16px', background: '#F0F4FF', borderTop: `2px solid #C8D3E8` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.blue, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        Resumo — {label}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', minWidth: 900 }}>
          <thead>
            <tr style={{ background: '#E8EEFA' }}>
              {cols.map(c => (
                <th key={c.label} style={{ padding: '6px 10px', fontWeight: 700, fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', textAlign: 'right', borderBottom: `1px solid #C8D3E8` }}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {cols.map((c, i) => (
                <td key={i} style={{ padding: '8px 10px', fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: c.color ?? C.text, whiteSpace: 'nowrap' }}>
                  {c.value === null ? '—' : c.value === 0 ? '0,00' : fmt(Math.abs(c.value))}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const LinhaALinhaSection = ({
  finance, salesData, salesNotConciliated, onExportSaquePDF, onViewUserSales,
}: {
  finance: InvoiceFinanceItem[];
  salesData: InvoiceSaleItem[];
  salesNotConciliated: InvoiceNotConciliatedSale[];
  onExportSaquePDF: (saqueIndex: number) => void;
  onViewUserSales: (clientId: string | number, clientName: string) => void;
}) => {
  const saleByHashMap = new Map<string, InvoiceSaleItem>();
  for (const s of salesData) {
    if (s.invoice_hash) saleByHashMap.set(s.invoice_hash, s);
    if (s.sale_hash)    saleByHashMap.set(s.sale_hash, s);
  }

  const [expandedRows, setExpandedRows] = useState<Set<number | string>>(new Set());
  const [collapsedPeriods, setCollapsedPeriods] = useState<Set<number>>(new Set());

  const toggleRow    = (id: number | string) => setExpandedRows(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const togglePeriod = (idx: number)         => setCollapsedPeriods(prev => { const n = new Set(prev); n.has(idx) ? n.delete(idx) : n.add(idx); return n; });

  const ESTORNO_DEBITO  = new Set(['chargeback', 'refund']);
  const ESTORNO_CREDITO = new Set(['chargeback_refund']);

  const allRows = [
    ...finance.map(f => ({
      kind: 'finance' as const, data: f,
      sortDate: new Date(f.dateRef ?? f.date).getTime(),
    })),
    ...salesNotConciliated
      .filter(s => !s.isPastDeadline)
      .map(s => ({
        kind: 'sale' as const, data: s,
        sortDate: s.estimatedAvailableAt?.getTime() ?? new Date(s.sale.approved_at ?? '').getTime(),
      })),
  ].sort((a, b) => a.sortDate - b.sortDate);

  let runningSaldo = 0;
  const rowsWithSaldo = allRows.map(row => {
    if (row.kind === 'finance') {
      const f = row.data as InvoiceFinanceItem;
      if (f.operation_type_raw != null) runningSaldo = f.saldo ?? runningSaldo;
      return { ...row, saldoExibido: runningSaldo, isProjected: false };
    } else {
      runningSaldo += (row.data as InvoiceNotConciliatedSale).sale.net_total ?? 0;
      return { ...row, saldoExibido: runningSaldo, isProjected: true };
    }
  });

  type RowWithSaldo = typeof rowsWithSaldo[number];
  const periods: Array<{ rows: RowWithSaldo[]; hasSaque: boolean }> = [];
  let currentPeriod: RowWithSaldo[] = [];
  for (const row of rowsWithSaldo) {
    currentPeriod.push(row);
    if (row.kind === 'finance' && (row.data as InvoiceFinanceItem).operation_type_raw === 'ted') {
      periods.push({ rows: currentPeriod, hasSaque: true }); currentPeriod = [];
    }
  }
  if (currentPeriod.length > 0) periods.push({ rows: currentPeriod, hasSaque: false });

  const periodStartSaldos = periods.map((_, i) => {
    if (i === 0) return 0;
    const lastRow = periods[i - 1].rows[periods[i - 1].rows.length - 1];
    return lastRow.saldoExibido;
  });

  const computeSummary = (period: { rows: RowWithSaldo[] }, startSaldo: number): PeriodSummaryFaturas => {
    const credits = period.rows.filter(r => r.kind === 'finance' && (r.data as InvoiceFinanceItem).operation_type_raw === 'credit');
    const tedRow  = period.rows.find(r => r.kind === 'finance' && (r.data as InvoiceFinanceItem).operation_type_raw === 'ted');
    const ted     = tedRow ? (tedRow.data as InvoiceFinanceItem) : null;

    const bruto            = credits.reduce((a, r) => a + ((r.data as InvoiceFinanceItem).total ?? 0), 0);
    const installmentValue = credits.reduce((a, r) => a + ((r.data as InvoiceFinanceItem).installment_value ?? 0), 0);
    const jurosMulta       = bruto - installmentValue;
    const jurosParcelamento= credits.reduce((a, r) => a + ((r.data as InvoiceFinanceItem).fees ?? 0), 0);
    const taxaTotal        = credits.reduce((a, r) => a + ((r.data as InvoiceFinanceItem).tax ?? 0), 0);
    const liquido          = credits.reduce((a, r) => a + ((r.data as InvoiceFinanceItem).net_total ?? 0), 0);
    const taxaSaque        = ted?.operation_fee ?? 0;
    const valorSaque       = ted ? Math.abs(ted.net_total ?? 0) : 0;
    const saldoFinal       = period.rows[period.rows.length - 1]?.saldoExibido ?? 0;

    return { bruto, sobrantesInicio: startSaldo, installmentValue, jurosMulta, jurosParcelamento, taxaTotal, liquido, taxaSaque, valorSaque, saldoFinal };
  };

  let saqueIndex = 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', padding: '10px 0' }}>
        <LegendaDot color="#059669" label="Saque (TED)" />
        <LegendaDot color="#D97706" label="Não sacado (crédito pendente)" />
        <LegendaDot color="#6366F1" label="Aguardando conciliação" />
        <LegendaDot color="#C0392B" label="Chargeback / Estorno" />
        <LegendaDot color="#F97316" label="Estorno revertido" />
        <LegendaDot color="#94A3B8" label="Op. interna" />
      </div>

      {periods.map((period, pIdx) => {
        const isCollapsed = collapsedPeriods.has(pIdx);
        const startSaldo  = periodStartSaldos[pIdx];
        const summary     = computeSummary(period, startSaldo);
        const tedEntry    = period.rows.find(r => r.kind === 'finance' && (r.data as InvoiceFinanceItem).operation_type_raw === 'ted');
        const tedDate     = tedEntry ? fmtDate((tedEntry.data as InvoiceFinanceItem).date) : null;
        const periodLabel = tedDate ? `Período até saque de ${tedDate}` : 'Período atual (sem saque)';
        const localSaqueIdx = period.hasSaque ? saqueIndex + 1 : null;
        if (period.hasSaque) saqueIndex += 1;

        return (
          <Card key={pIdx}>
            <div
              onClick={() => togglePeriod(pIdx)}
              style={{ padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', background: period.hasSaque ? C.blueLight : C.bg, borderBottom: isCollapsed ? 'none' : `1px solid ${C.border}` }}
            >
              <span style={{ fontSize: 13, color: C.muted }}>{isCollapsed ? '▶' : '▼'}</span>
              <span style={{ fontWeight: 700, fontSize: 13, color: C.text, flex: 1 }}>{periodLabel}</span>
              {localSaqueIdx && (
                <>
                  <FaFilePdf size={14} title={`Exportar PDF — Saque ${localSaqueIdx}`}
                    style={{ cursor: 'pointer', color: C.red, flexShrink: 0 }}
                    onClick={e => { e.stopPropagation(); onExportSaquePDF(localSaqueIdx); }}
                  />
                </>
              )}
              <Pill label={`${period.rows.filter(r => r.kind === 'finance').length} entradas`} color={C.blue} bg={C.blueLight} />
              <Pill label={`Bruto: ${fmt(summary.bruto)}`} color={C.green} bg={C.greenBg} />
              <Pill label={`Saldo: ${fmt(summary.saldoFinal)}`} color={summary.saldoFinal < 0 ? C.red : C.muted} bg={summary.saldoFinal < 0 ? C.redBg : '#F8FAFC'} />
            </div>

            {!isCollapsed && (
              <>
                <Table headers={['Aprovação', 'Conciliação', 'Operação', 'Cliente', 'Método', 'Parcela', 'Juros/Multa', 'Bruto', 'Líquido', 'Saldo', 'Situação']}>
                  {period.rows.map((row, idx) => {
                    if (row.kind === 'sale') {
                      const { sale, estimatedAvailableAt } = row.data as InvoiceNotConciliatedSale;
                      return (
                        <tr key={`s-${sale._id}-${pIdx}-${idx}`} style={{ background: '#F5F3FF' }}>
                          <Td nowrap muted>{fmtDateTime(sale.approved_at)}</Td>
                          <Td nowrap muted><span style={{ color: '#6366F1', fontSize: 11 }}>{estimatedAvailableAt ? fmtDate(estimatedAvailableAt.toISOString()) : '—'}</span><div style={{ fontSize: 9, color: '#A5B4FC' }}>estimado</div></Td>
                          <Td><span style={{ color: '#6366F1', fontWeight: 700 }}>🕐 Ag. Conciliação</span></Td>
                          <Td bold>{sale.payer_id_response?.name ?? sale.client_id_response?.name ?? '—'}</Td>
                          <Td muted>{sale.payment_method_id}</Td>
                          <Td align="right">{fmt(sale.installment_value ?? 0)}</Td>
                          <Td align="right" muted>—</Td>
                          <Td align="right">{fmt(sale.total)}</Td>
                          <Td align="right">{fmt(sale.net_total)}</Td>
                          <Td align="right"><span style={{ color: '#6366F1', fontWeight: 700 }}>{fmt(row.saldoExibido)}</span><div style={{ fontSize: 9, color: '#A5B4FC' }}>projetado</div></Td>
                          <Td><Pill label="Aguardando" color="#5B21B6" bg="#EDE9FE" /></Td>
                        </tr>
                      );
                    }

                    const item = row.data as InvoiceFinanceItem;
                    const isCredit  = item.operation_type_raw === 'credit';
                    const isTed     = item.operation_type_raw === 'ted';
                    const isDebito  = ESTORNO_DEBITO.has(item.operation_type_raw!);
                    const isRefund  = ESTORNO_CREDITO.has(item.operation_type_raw!);
                    const isExpanded = expandedRows.has(item.item_id);

                    const rowBg = isTed ? '#F0FDF4' : isDebito ? '#FFF1F2' : isRefund ? '#FFF7ED' : item.operation_type_raw === 'internal_transfer' ? '#F8FAFC' : 'transparent';

                    const saleRef = saleByHashMap.get(item.hash);
                    const cid     = (item as any).client_id ?? (saleRef as any)?.client_id ?? (saleRef as any)?.payer_id;
                    const cname   = item.client || saleRef?.payer_id_response?.name || saleRef?.client_id_response?.name || 'Cliente';

                    // Juros/multa de atraso = total − installment_value (calculado por linha)
                    const installLinhaV = item.installment_value ?? 0;
                    const totalLinhaV   = item.total ?? 0;
                    const jurosMultaLinha = totalLinhaV - installLinhaV;

                    return (
                      <React.Fragment key={`f-${item.item_id}-${pIdx}-${idx}`}>
                        <tr
                          style={{ background: rowBg, borderBottom: `1px solid ${isTed ? C.border : '#EEF2F8'}`, fontWeight: isTed ? 700 : undefined, cursor: isCredit ? 'pointer' : undefined }}
                          onClick={isCredit ? () => toggleRow(item.item_id) : undefined}
                        >
                          <Td nowrap muted>{fmtDateTime(item.dateRef ?? item.date)}</Td>
                          <Td nowrap muted>{fmtDate(item.date)}</Td>
                          <Td color={isTed ? C.green : isDebito ? C.red : undefined}>
                            {item.operation_type || item.operation_type_raw || '—'}
                          </Td>
                          <Td bold>{item.client || '—'}</Td>
                          <Td muted nowrap>{item.payment_method_id || '—'}</Td>
                          <Td align="right" color={isTed ? C.red : undefined}>{isTed ? `−${fmt(Math.abs(item.net_total))}` : installLinhaV > 0 ? fmt(installLinhaV) : '—'}</Td>
                          <Td align="right" color={jurosMultaLinha > 0 ? C.green : C.muted}>{jurosMultaLinha > 0 ? `+${fmt(jurosMultaLinha)}` : '—'}</Td>
                          <Td align="right">{item.total != null ? fmt(item.total) : '—'}</Td>
                          <Td align="right" color={item.net_total >= 0 ? C.green : C.red}>{item.net_total != null ? fmt(Math.abs(item.net_total)) : '—'}</Td>
                          <Td align="right" bold color={row.saldoExibido < 0 ? C.red : undefined}>{fmt(row.saldoExibido)}</Td>
                          <Td>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                              {isTed    && <Pill label="Saque (TED)"  color={C.green}  bg={C.greenBg} />}
                              {isDebito && <Pill label="Chargeback"   color={C.red}    bg={C.redBg} />}
                              {isRefund && <Pill label="Estorno CB"   color={C.amber}  bg={C.amberBg} />}
                              {isCredit && <span style={{ fontSize: 10, color: C.muted }}>{isExpanded ? '▲' : '▼'}</span>}
                              {cid && isCredit && (
                                <button
                                  title="Ver histórico do cliente"
                                  onClick={e => { e.stopPropagation(); onViewUserSales(cid, cname); }}
                                  style={{ border: 'none', background: C.blueLight, cursor: 'pointer', padding: '3px 9px', borderRadius: 6, fontSize: 11, fontWeight: 700, color: C.blue, display: 'flex', alignItems: 'center', gap: 4 }}
                                >
                                  👤 Ver compras
                                </button>
                              )}
                            </div>
                          </Td>
                        </tr>

                        {isCredit && isExpanded && (
                          <tr key={`detail-${item.item_id}`}>
                            <td colSpan={11} style={{ padding: '8px 20px 14px 32px', background: '#FAFBFD', borderBottom: `1px solid ${C.border}` }}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px 20px', padding: '10px 14px', background: '#fff', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12 }}>
                                <Stat label="Parcela"              value={fmt(item.installment_value ?? 0)}           size="sm" accent={C.text} />
                                <Stat label="Juros/Multa Atraso"   value={jurosMultaLinha > 0 ? `+${fmt(jurosMultaLinha)}` : '—'} size="sm" accent={jurosMultaLinha > 0 ? C.green : C.muted} />
                                <Stat label="Bruto Total"          value={fmt(item.total ?? 0)}                       size="sm" accent={C.text} />
                                <Stat label="Juros parcelamento"   value={`−${fmt(item.fees ?? 0)}`}                  size="sm" accent={C.red} />
                                <Stat label="Taxa de cartão"       value={`−${fmt(item.transaction_tax ?? 0)}`}       size="sm" accent={C.red} />
                                <Stat label="Taxa administrativa"  value={`−${fmt(item.administration_tax ?? 0)}`}    size="sm" accent={C.red} />
                                <Stat label="Taxa total (gateway)" value={`−${fmt(item.tax ?? 0)}`}                   size="sm" accent={C.red} />
                                <Stat label="Líquido recebido"     value={fmt(item.net_total)}                        size="sm" accent={C.green} />
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </Table>
                <SaquePeriodSummary s={summary} label={periodLabel} />
              </>
            )}
          </Card>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Capa do relatório (Resumo)
// ─────────────────────────────────────────────────────────────────────────────

const ReportOverview = ({
  r, finance, salesNotConciliated, onExportSaquePDF,
}: {
  r: InvoiceReport;
  finance: InvoiceFinanceItem[];
  salesNotConciliated: InvoiceNotConciliatedSale[];
  onExportSaquePDF: (saqueIndex: number) => void;
}) => {
  const [expandedSaque, setExpandedSaque] = useState<number | null>(null);

  const allRows = [
    ...finance.map(f => ({ kind: 'finance' as const, data: f, sortDate: new Date(f.dateRef ?? f.date).getTime() })),
    ...salesNotConciliated.filter(s => !s.isPastDeadline).map(s => ({
      kind: 'sale' as const, data: s,
      sortDate: s.estimatedAvailableAt?.getTime() ?? new Date(s.sale.approved_at ?? '').getTime(),
    })),
  ].sort((a, b) => a.sortDate - b.sortDate);

  let runningSaldo = 0;
  const rowsWithSaldo = allRows.map(row => {
    if (row.kind === 'finance') {
      const f = row.data as InvoiceFinanceItem;
      if (f.operation_type_raw != null) runningSaldo = f.saldo ?? runningSaldo;
      return { ...row, saldoExibido: runningSaldo };
    } else {
      runningSaldo += (row.data as InvoiceNotConciliatedSale).sale.net_total ?? 0;
      return { ...row, saldoExibido: runningSaldo };
    }
  });

  const periods: Array<{ rows: typeof rowsWithSaldo; hasSaque: boolean }> = [];
  let cur: typeof rowsWithSaldo = [];
  for (const row of rowsWithSaldo) {
    cur.push(row);
    if (row.kind === 'finance' && (row.data as InvoiceFinanceItem).operation_type_raw === 'ted') {
      periods.push({ rows: cur, hasSaque: true }); cur = [];
    }
  }
  if (cur.length > 0) periods.push({ rows: cur, hasSaque: false });

  const ESTORNO_DEBITO  = new Set(['chargeback', 'refund']);
  const ESTORNO_CREDITO = new Set(['chargeback_refund']);

  const rowColor = (f: InvoiceFinanceItem) => {
    if (f.operation_type_raw === 'ted')              return '#F0FDF4';
    if (ESTORNO_DEBITO.has(f.operation_type_raw!))   return '#FFF1F2';
    if (ESTORNO_CREDITO.has(f.operation_type_raw!))  return '#FFF7ED';
    if (f.operation_type_raw === 'internal_transfer') return '#F8FAFC';
    return 'transparent';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <ReconciliationBanner r={r} />

      {/* Cards de alto nível */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        {[
          { label: 'Bruto Arrecadado',   value: fmt(r.brutoConciliated),            accent: C.blue },
          { label: 'Parcelas',           value: fmt(r.totalInstallmentValue),        accent: C.blue,   sub: r.totalJurosMulta > 0 ? `+ ${fmt(r.totalJurosMulta)} juros/multa` : undefined },
          { label: 'Não Conciliado',     value: fmt(r.totalNotConciliated),          accent: r.totalNotConciliated > 0 ? C.red : C.muted, sub: `${r.salesNotConciliated.length} faturas` },
          { label: 'Líquido Final',      value: fmt(r.liquidoTotal),                 accent: C.green },
          { label: 'Já Sacado',          value: fmt(r.totalSacado),                  accent: C.purple, sub: `${r.saqueEntries.length} saques` },
          { label: 'Saldo Disponível',   value: fmt(r.saldoAtual),                   accent: r.saldoAtual < 0 ? C.red : C.purple },
        ].map(c => (
          <Card key={c.label} style={{ padding: 16, borderTop: `3px solid ${c.accent}` }}>
            <Stat label={c.label} value={c.value} sub={(c as any).sub} accent={c.accent} size="md" />
          </Card>
        ))}
      </div>

      <BrutoTable r={r} />
      <DreSection r={r} />

      {/* ── Saques Realizados ─────────────────────────────────────────── */}
      {r.saqueEntries.length > 0 && (
        <Card>
          <CardHeader
            label={`${r.saqueEntries.length} Saque${r.saqueEntries.length !== 1 ? 's' : ''} Realizado${r.saqueEntries.length !== 1 ? 's' : ''}`}
            accent={C.green}
            right={<span style={{ fontSize: 12, color: C.muted }}>Total sacado: <strong style={{ color: C.purple }}>{fmt(r.totalSacado)}</strong></span>}
          />
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr>
                  {['#', 'Data do Saque', 'Bruto do Período', 'Taxa TED', 'Líquido Sacado', 'Saldo Após', 'Linha a Linha', 'PDF'].map(h => (
                    <th key={h} style={{ padding: '9px 14px', background: '#F1F5FB', color: C.muted, fontWeight: 700, fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: ['Bruto do Período', 'Taxa TED', 'Líquido Sacado', 'Saldo Após'].includes(h) ? 'right' : 'left', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {r.saqueEntries.map((saque, si) => {
                  const period       = periods[si];
                  const isOpen       = expandedSaque === si;
                  const credits      = period?.rows.filter(r => r.kind === 'finance' && (r.data as InvoiceFinanceItem).operation_type_raw === 'credit') ?? [];
                  const brutoPeriodo = credits.reduce((a, r) => a + ((r.data as InvoiceFinanceItem).total ?? 0), 0);
                  const saldoApos    = saque.saldo ?? 0;

                  return (
                    <React.Fragment key={saque.item_id}>
                      <tr style={{ background: isOpen ? C.blueLight : 'transparent', borderBottom: `1px solid ${C.border}` }}>
                        <Td muted bold>{si + 1}</Td>
                        <Td nowrap bold>{fmtDateTime(saque.dateRef ?? saque.date)}</Td>
                        <Td align="right">{fmt(brutoPeriodo)}</Td>
                        <Td align="right" color={C.red}>{saque.operation_fee > 0 ? `−${fmt(saque.operation_fee)}` : '—'}</Td>
                        <Td align="right" color={C.green} bold>{fmt(Math.abs(saque.net_total ?? 0))}</Td>
                        <Td align="right" color={saldoApos < 0 ? C.red : C.muted}>{fmt(saldoApos)}</Td>
                        <Td>
                          <button
                            onClick={() => setExpandedSaque(isOpen ? null : si)}
                            style={{ display: 'flex', alignItems: 'center', gap: 5, border: 'none', background: isOpen ? C.blue : C.blueLight, color: isOpen ? '#fff' : C.blue, fontWeight: 700, fontSize: 11, borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}
                          >
                            {isOpen ? '▲ Fechar' : '▼ Ver linhas'}
                          </button>
                        </Td>
                        <Td>
                          <button
                            onClick={() => onExportSaquePDF(si + 1)}
                            style={{ display: 'flex', alignItems: 'center', gap: 5, border: 'none', background: C.redBg, color: C.red, fontWeight: 700, fontSize: 11, borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}
                          >
                            <FaFilePdf size={12} /> PDF
                          </button>
                        </Td>
                      </tr>

                      {isOpen && period && (
                        <tr>
                          <td colSpan={8} style={{ padding: 0, borderBottom: `2px solid ${C.border}` }}>
                            <div style={{ background: C.blueLight, padding: '0 0 4px 0' }}>
                              <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                  <thead>
                                    <tr style={{ background: C.blueLight }}>
                                      {['Aprovação', 'Conciliação', 'Operação', 'Cliente', 'Método', 'Parcela', 'Juros/Multa', 'Bruto', 'Líquido', 'Saldo', 'Situação'].map(h => (
                                        <th key={h} style={{ padding: '7px 12px', color: C.blue, fontWeight: 700, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: ['Parcela', 'Juros/Multa', 'Bruto', 'Líquido', 'Saldo'].includes(h) ? 'right' : 'left', borderBottom: `1px solid #C8D3E8`, whiteSpace: 'nowrap' }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {period.rows.map((row, ri) => {
                                      if (row.kind === 'sale') {
                                        const { sale } = row.data as InvoiceNotConciliatedSale;
                                        return (
                                          <tr key={`sale-${ri}`} style={{ background: '#F5F3FF', borderBottom: `1px solid #E0E7FF` }}>
                                            <td style={{ padding: '7px 12px', color: C.muted, fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDateTime(sale.approved_at)}</td>
                                            <td style={{ padding: '7px 12px', color: '#6366F1', fontSize: 11 }}>estimado</td>
                                            <td style={{ padding: '7px 12px', color: '#6366F1', fontWeight: 700 }}>🕐 Ag. Conciliação</td>
                                            <td style={{ padding: '7px 12px', fontWeight: 600 }}>{sale.payer_id_response?.name ?? sale.client_id_response?.name ?? '—'}</td>
                                            <td style={{ padding: '7px 12px', color: C.muted }}>{sale.payment_method_id}</td>
                                            <td style={{ padding: '7px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(sale.installment_value ?? 0)}</td>
                                            <td style={{ padding: '7px 12px', textAlign: 'right', color: C.muted }}>—</td>
                                            <td style={{ padding: '7px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(sale.total)}</td>
                                            <td style={{ padding: '7px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(sale.net_total)}</td>
                                            <td style={{ padding: '7px 12px', textAlign: 'right', color: '#6366F1', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt(row.saldoExibido)}</td>
                                            <td style={{ padding: '7px 12px' }}><Pill label="Aguardando" color="#5B21B6" bg="#EDE9FE" /></td>
                                          </tr>
                                        );
                                      }
                                      const item  = row.data as InvoiceFinanceItem;
                                      const isTed = item.operation_type_raw === 'ted';
                                      const bg    = rowColor(item);
                                      const installV  = item.installment_value ?? 0;
                                      const totalV    = item.total ?? 0;
                                      const jurosV    = totalV - installV;
                                      return (
                                        <tr key={`fi-${item.item_id}-${ri}`} style={{ background: bg, borderBottom: `1px solid ${isTed ? C.border : '#EEF2F8'}`, fontWeight: isTed ? 700 : undefined }}>
                                          <td style={{ padding: '7px 12px', color: C.muted, fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDateTime(item.dateRef ?? item.date)}</td>
                                          <td style={{ padding: '7px 12px', color: C.muted, fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDate(item.date)}</td>
                                          <td style={{ padding: '7px 12px', color: isTed ? C.green : C.text, fontWeight: isTed ? 800 : undefined }}>{item.operation_type || item.operation_type_raw || '—'}</td>
                                          <td style={{ padding: '7px 12px' }}>{item.client || '—'}</td>
                                          <td style={{ padding: '7px 12px', color: C.muted }}>{item.payment_method_id || '—'}</td>
                                          <td style={{ padding: '7px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{installV > 0 ? fmt(installV) : '—'}</td>
                                          <td style={{ padding: '7px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: jurosV > 0 ? C.green : C.muted }}>{jurosV > 0 ? `+${fmt(jurosV)}` : '—'}</td>
                                          <td style={{ padding: '7px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{item.total != null ? fmt(item.total) : '—'}</td>
                                          <td style={{ padding: '7px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: item.net_total >= 0 ? C.green : C.red }}>{item.net_total != null ? fmt(Math.abs(item.net_total)) : '—'}</td>
                                          <td style={{ padding: '7px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmt(row.saldoExibido)}</td>
                                          <td style={{ padding: '7px 12px' }}>
                                            {isTed && <Pill label="Saque (TED)" color={C.green} bg={C.greenBg} />}
                                            {ESTORNO_DEBITO.has(item.operation_type_raw!) && <Pill label="Chargeback" color={C.red} bg={C.redBg} />}
                                            {ESTORNO_CREDITO.has(item.operation_type_raw!) && <Pill label="Estorno CB" color={C.amber} bg={C.amberBg} />}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Alertas */}
      {r.chargebacks.length > 0 && (
        <div style={{ background: C.redBg, border: `1px solid #FECACA`, borderRadius: 10, padding: '12px 16px', display: 'flex', gap: 10 }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <div>
            <div style={{ fontWeight: 700, color: C.red, fontSize: 13 }}>
              {r.chargebacks.length} chargeback{r.chargebacks.length !== 1 ? 's' : ''} · custo total de operação: {fmt(r.totalCustoOperacaoChargebacks)}
            </div>
            <div style={{ color: C.red, fontSize: 12, marginTop: 2 }}>Veja a aba "Divergências" para o detalhamento.</div>
          </div>
        </div>
      )}
      {r.salesNotConciliated.filter(s => s.isPastDeadline).length > 0 && (
        <div style={{ background: C.amberBg, border: `1px solid #FCD34D`, borderRadius: 10, padding: '12px 16px', display: 'flex', gap: 10 }}>
          <span style={{ fontSize: 18 }}>🔍</span>
          <div>
            <div style={{ fontWeight: 700, color: C.amber, fontSize: 13 }}>
              {r.salesNotConciliated.filter(s => s.isPastDeadline).length} fatura(s) pagas fora do prazo sem conciliação
            </div>
            <div style={{ color: C.amber, fontSize: 12, marginTop: 2 }}>
              Verifique se foram liberadas manualmente ou perderam o relacionamento com o meio de pagamento.
            </div>
          </div>
        </div>
      )}

      {/* Inconsistências ao fim da capa */}
      <SaldoInconsistenciesSection items={r.saldoInconsistencies} />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Componente principal
// ─────────────────────────────────────────────────────────────────────────────

type TabId = 'resumo' | 'nao_conciliadas' | 'divergencias' | 'linha_linha';

const FinanceReportFaturasComponent = () => {
  const {open} = useTransferPopup();
  const { configuration, projectHash } = useConfiguration();
  const { addToast } = useToast();
  const { loadGlobal, salesData, financeData, report, loading } = useFinanceCheckFaturas();
  const [tab, setTab]       = useState<TabId>('resumo');
  const [showAI, setShowAI] = useState(false);
  const [drawer, setDrawer] = useState<{ clientId: string | number; clientName: string } | null>(null);
  const openDrawer = (clientId: string | number, clientName: string) => setDrawer({ clientId, clientName });
  const currentProjectUrl = configuration?.url ?? '';

  useEffect(() => {
    if (configuration?.url) loadGlobal(configuration.url);
  }, [configuration?.url]);

  // ── Exportação PDF geral ──────────────────────────────────────────────────
  const gerarPDF = async () => {
    addToast({ title: 'Gerando PDF…', type: 'info' });
    const project = (await api.get(`/projects`, { params: { where: { url: projectHash } } }))?.data?.rows?.[0];
    const response = await api.get(`/check-saldo-static/${project.url}/faturas/SaleInvoice/json`);
    handleFinanceReportPDF({ data: response?.data, project });
  };

  // ── Exportação PDF por saque ──────────────────────────────────────────────
  const gerarPDFSaque = async (saque: number) => {
    addToast({ title: `Gerando PDF do saque ${saque}…`, type: 'info' });
    const project = (await api.get(`/projects`, { params: { where: { url: projectHash } } }))?.data?.rows?.[0];
    const response = await api.get(`/check-saldo-static/${project.url}/faturas/SaleInvoice/json`);
    handleFinanceReportPDFWithSaque({ data: response?.data, project, saque });
  };

  // ── Exportação Excel ──────────────────────────────────────────────────────
  const gerarExcel = async () => {
    addToast({ title: 'Gerando Excel…', type: 'info' });
    await downloadit({ url: `/check-saldo-static/${projectHash}/faturas/SaleInvoice/xlsx` });
  };

  if (loading || !report) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 260, gap: 14 }}>
        <div style={{ width: 38, height: 38, borderRadius: '50%', border: `3px solid #E3E8F0`, borderTopColor: '#1E3A5F', animation: 'spin 0.8s linear infinite' }} />
        <span style={{ color: '#8693A8', fontSize: 14 }}>Carregando dados de faturas…</span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const tabs: { id: TabId; label: string; badge?: number }[] = [
    { id: 'resumo',          label: 'Relatório Geral' },
    { id: 'nao_conciliadas', label: 'Não Conciliadas', badge: report.salesNotConciliated.length },
    { id: 'divergencias',    label: 'Divergências',    badge: report.chargebacks.length },
    { id: 'linha_linha',     label: 'Linha a Linha',   badge: financeData.length },
  ];

  return (
    <div style={{ fontFamily: "'DM Sans','Helvetica Neue',Arial,sans-serif", background: '#F4F6FA', minHeight: '100vh', padding: '28px 20px' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        tr:hover td { background: #F7F9FD; }
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
      `}</style>

      {drawer && (
        <UserSalesDrawer
          clientId={drawer.clientId}
          clientName={drawer.clientName}
          currentProjectUrl={currentProjectUrl}
          onClose={() => setDrawer(null)}
        />
      )}

      {showAI && (
        <AIAnalysisPanel
          report={report}
          financeData={financeData}
          onClose={() => setShowAI(false)}
        />
      )}

      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Header */}
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#1A2236', letterSpacing: '-0.03em' }}>Relatório de Faturas</h1>
          <p style={{ margin: '4px 0 0', color: '#8693A8', fontSize: 13 }}>Conciliação e análise de parcelas e faturas recorrentes</p>
        </div>

        {/* Barra de tabs + botões */}
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 4, background: '#FFFFFF', border: `1px solid #E3E8F0`, borderRadius: 10, padding: 4, flexWrap: 'wrap' }}>
            {tabs.map(t => <TabBtn key={t.id} label={t.label} active={tab === t.id} badge={t.badge} onClick={() => setTab(t.id)} />)}
          </div>

          <div style={{ width: 1, height: 32, background: '#E3E8F0', margin: '0 4px' }} />

          <button onClick={gerarPDF} title="Exportar PDF"
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: `1px solid #E3E8F0`, background: '#fff', color: '#C0392B', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
            <FaFilePdf size={14} /> PDF
          </button>
          <button onClick={gerarExcel} title="Exportar Excel"
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: `1px solid #E3E8F0`, background: '#fff', color: '#0A7A55', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
            <FaFileExcel size={14} /> Excel
          </button>
       <button onClick={() => open(`${configuration?.url}-invoice`)} title="Exportar Excel"
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: `1px solid #E3E8F0`, background: '#fff', color: '#0A7A55', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
            <FaWallet size={14} /> Transferências
          </button>


          <button onClick={() => setShowAI(true)} title="Arquivo de auditoria JSON"
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: 'none', background: '#5B21B6', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
            ⬇ JSON para auditoria
          </button>
        </div>

        {/* Conteúdo das abas */}
        {tab === 'resumo' && (
          <ReportOverview
            r={report}
            finance={financeData}
            salesNotConciliated={report.salesNotConciliated}
            onExportSaquePDF={gerarPDFSaque}
          />
        )}
        {tab === 'nao_conciliadas' && <NotConciliatedSection items={report.salesNotConciliated} />}
        {tab === 'divergencias' && (
          <DivergenceSection
            chargebacks={report.chargebacks}
            creditosOrfaos={report.creditosOrfaos}
            onViewUserSales={openDrawer}
          />
        )}
        {tab === 'linha_linha' && (
          <LinhaALinhaSection
            finance={financeData}
            salesData={salesData}
            salesNotConciliated={report.salesNotConciliated}
            onExportSaquePDF={gerarPDFSaque}
            onViewUserSales={openDrawer}
          />
        )}
      </div>
    </div>
  );
};

export default FinanceReportFaturasComponent;
